import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Mail, Clock, Heart } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Message sent — Papa G will get back to you soon!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16" data-testid="contact-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">GET IN TOUCH</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--navy)] mt-3 leading-tight">
            Got a custom idea?<br/>Let&apos;s make it.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)]">
            Want something one-of-a-kind — a bulk order for the team, a memorial design,
            a wedding favor, or just a wild idea? Send it our way and Papa G will get
            right back to you.
          </p>
          <div className="mt-6 card-rustic p-5 border-l-4 border-[var(--gold)]" data-testid="contact-bulk-callout">
            <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--crimson)]">Bulk &amp; Wholesale</div>
            <p className="mt-2 text-[var(--navy)] leading-relaxed">
              Ordering in quantity for a team, church, reunion, or business? <span className="font-semibold">Contact us for a custom quote</span> — we offer special pricing on bulk runs.
            </p>
          </div>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 grid place-items-center rounded-sm bg-[var(--navy)] text-[var(--bone)]"><Mail className="w-5 h-5"/></div>
              <div>
                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">Email</div>
                <div className="text-[var(--navy)]">contact@papagshop.com</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 grid place-items-center rounded-sm bg-[var(--navy)] text-[var(--bone)]"><Clock className="w-5 h-5"/></div>
              <div>
                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">Hours</div>
                <div className="text-[var(--navy)]">Mon–Sat · 9–6 CST</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 grid place-items-center rounded-sm bg-[var(--gold)] text-[var(--navy)]"><Heart className="w-5 h-5"/></div>
              <div>
                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">Made by</div>
                <div className="text-[var(--navy)]">Papa G &amp; MawMaw · East Texas</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <div className="card-rustic p-12 text-center" data-testid="contact-success">
              <div className="w-16 h-16 mx-auto grid place-items-center rounded-full bg-[var(--gold)] text-[var(--navy)]"><Send className="w-7 h-7"/></div>
              <h2 className="font-serif-display text-3xl text-[var(--navy)] mt-6">Message on its way!</h2>
              <p className="mt-3 text-[var(--ink-soft)]">Thanks for reaching out. We&apos;ll reply to your email as soon as we can.</p>
              <button onClick={() => setSent(false)} className="btn-ghost mt-8" data-testid="contact-send-another">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} className="card-rustic p-6 lg:p-8 space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Your name" required value={form.name} onChange={onChange("name")} testId="contact-name"/>
                <Field label="Email" required type="email" value={form.email} onChange={onChange("email")} testId="contact-email"/>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Phone (optional)" value={form.phone} onChange={onChange("phone")} testId="contact-phone"/>
                <Field label="Subject (optional)" value={form.subject} onChange={onChange("subject")} testId="contact-subject"/>
              </div>
              <label className="block">
                <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1.5 block">Tell us what you&apos;re thinking<span className="text-[var(--crimson)] ml-1">*</span></span>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={6}
                  maxLength={4000}
                  required
                  placeholder="Describe your custom request — quantity, colors, sizes, artwork ideas, deadlines…"
                  className="input-field resize-y"
                />
              </label>
              <button type="submit" disabled={sending} className="btn-primary btn-crimson w-full justify-center" data-testid="contact-submit">
                <Send className="w-4 h-4"/> {sending ? "Sending…" : "Send Request"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testId }) {
  return (
    <label className="block">
      <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1.5 block">{label}{required && <span className="text-[var(--crimson)] ml-1">*</span>}</span>
      <input data-testid={testId} type={type} required={required} value={value} onChange={onChange} className="input-field"/>
    </label>
  );
}
